import type {
	IFiscalCalculation,
	IFiscalCalculationItem,
	IFiscalCalculationItemRequest,
	IFiscalCalculationRequest,
	IFiscalTaxAmounts,
	IFiscalTaxDetail,
	TFiscalTaxType,
} from '@interfaces/fiscal';

export interface IFiscalSummaryTotals {
	icmsAmount: number;
	icmsStAmount: number;
	ipiAmount: number;
	cbsAmount: number;
	ibsAmount: number;
	/** Sum of all tax amounts (informational). */
	totalTaxesAmount: number;
	/** Taxes with addToTotal=true (portion added to merchandise). */
	addedToTotalAmount: number;
	grandTotalAmount: number | null;
}

export function unwrapFiscalCalculation(
	payload:
		| IFiscalCalculation
		| { result?: IFiscalCalculation }
		| null
		| undefined
): IFiscalCalculation | null {
	if (!payload) return null;
	if ('result' in payload && payload.result) {
		return payload.result;
	}
	return payload as IFiscalCalculation;
}

export function buildFiscalCalculationItems(
	products: Array<{ productId: number; quantity: number; unitPrice: number }>
): IFiscalCalculationItemRequest[] {
	return products.map(item => ({
		productId: item.productId,
		quantity: item.quantity,
		unitPrice: item.unitPrice,
	}));
}

export function buildFiscalCalculationRequest(params: {
	tenantId: number;
	storeId: number;
	clientId: number;
	orderTypeId: number;
	sellingOperationId?: number | null;
	products: Array<{ productId: number; quantity: number; unitPrice: number }>;
	issueDate?: string;
}): IFiscalCalculationRequest {
	return {
		tenantId: params.tenantId,
		storeId: params.storeId,
		clientId: params.clientId,
		orderTypeId: params.orderTypeId,
		sellingOperationId: params.sellingOperationId ?? null,
		issueDate: params.issueDate ?? new Date().toISOString(),
		items: buildFiscalCalculationItems(params.products),
	};
}

function normalizeTaxType(taxType?: string | null): string {
	return (taxType || '').trim().toUpperCase().replace(/_/g, '-');
}

function findTaxDetail(
	details: IFiscalTaxDetail[] | null | undefined,
	...types: TFiscalTaxType[]
): IFiscalTaxDetail | undefined {
	if (!details?.length) return undefined;
	const wanted = types.map(type => normalizeTaxType(type));
	return details.find(detail =>
		wanted.includes(normalizeTaxType(detail.taxType))
	);
}

/** Maps calculate item.taxDetails → flat fields for cart/buy. */
export function flattenTaxDetails(
	item?: IFiscalCalculationItem | null
): IFiscalTaxAmounts {
	if (!item) return clearTaxFields();

	const details = item.taxDetails || [];
	const icms = findTaxDetail(details, 'ICMS');
	const icmsSt = findTaxDetail(details, 'ICMS-ST', 'ICMSST');
	const ipi = findTaxDetail(details, 'IPI');
	const cbs = findTaxDetail(details, 'CBS');
	const ibs = findTaxDetail(details, 'IBS');

	return {
		icmsAmount: icms?.taxAmount ?? null,
		icmsRate: icms?.appliedRate ?? null,
		icmsStAmount: icmsSt?.taxAmount ?? null,
		ipiAmount: ipi?.taxAmount ?? null,
		ipiRate: ipi?.appliedRate ?? null,
		cbsAmount: cbs?.taxAmount ?? null,
		cbsRate: cbs?.appliedRate ?? null,
		ibsAmount: ibs?.taxAmount ?? null,
		ibsRate: ibs?.appliedRate ?? null,
		taxDetailsJson: details.length ? JSON.stringify(details) : null,
	};
}

export function clearTaxFields(): IFiscalTaxAmounts {
	return {
		icmsAmount: null,
		icmsRate: null,
		icmsStAmount: null,
		ipiAmount: null,
		ipiRate: null,
		cbsAmount: null,
		cbsRate: null,
		ibsAmount: null,
		ibsRate: null,
		taxDetailsJson: null,
	};
}

export function mergeTaxesIntoProducts<T extends { productId: number }>(
	products: T[],
	calculation: IFiscalCalculation | null
): Array<T & IFiscalTaxAmounts> {
	const items = calculation?.items || [];
	const byProductId = new Map<number, IFiscalCalculationItem>();

	for (const item of items) {
		if (item?.productId != null) {
			byProductId.set(item.productId, item);
		}
	}

	return products.map(product => {
		const match = byProductId.get(product.productId);
		if (!match) {
			return {
				...product,
				...clearTaxFields(),
			};
		}

		return {
			...product,
			...flattenTaxDetails(match),
		};
	});
}

/**
 * Pairs each line with the calculate response in order.
 * Seller orders can repeat a product (different packing); productId alone would collapse them.
 * Falls back to productId when the response length does not match.
 */
export function mergeTaxesIntoLines<T extends { productId: number }>(
	lines: T[],
	calculation: IFiscalCalculation | null
): Array<T & IFiscalTaxAmounts> {
	const calculated = calculation?.items || [];
	if (!lines.length || calculated.length !== lines.length) {
		return mergeTaxesIntoProducts(lines, calculation);
	}

	return lines.map((line, index) => ({
		...line,
		...flattenTaxDetails(calculated[index]),
	}));
}

function sumTaxType(
	items: IFiscalCalculationItem[],
	...types: TFiscalTaxType[]
): number {
	const wanted = types.map(type => normalizeTaxType(type));
	return items.reduce((total, item) => {
		const amount = (item.taxDetails || [])
			.filter(detail => wanted.includes(normalizeTaxType(detail.taxType)))
			.reduce((sum, detail) => sum + (Number(detail.taxAmount) || 0), 0);
		return total + amount;
	}, 0);
}

function sumAddedToTotal(items: IFiscalCalculationItem[]): number {
	return items.reduce((total, item) => {
		const amount = (item.taxDetails || [])
			.filter(detail => detail.addToTotal)
			.reduce((sum, detail) => sum + (Number(detail.taxAmount) || 0), 0);
		return total + amount;
	}, 0);
}

export function getFiscalSummaryTotals(
	calculation: IFiscalCalculation | null,
	merchandiseTotal = 0
): IFiscalSummaryTotals {
	const items = calculation?.items || [];
	const icmsAmount = sumTaxType(items, 'ICMS');
	const icmsStAmount = sumTaxType(items, 'ICMS-ST', 'ICMSST');
	const ipiAmount = sumTaxType(items, 'IPI');
	const cbsAmount = sumTaxType(items, 'CBS');
	const ibsAmount = sumTaxType(items, 'IBS');
	const addedToTotalAmount = sumAddedToTotal(items);

	const totalTaxesAmount =
		calculation?.totalTaxesAmount != null
			? Number(calculation.totalTaxesAmount)
			: icmsAmount + icmsStAmount + ipiAmount + cbsAmount + ibsAmount;

	const grandTotalAmount =
		calculation?.grandTotalAmount != null
			? Number(calculation.grandTotalAmount)
			: merchandiseTotal + addedToTotalAmount;

	return {
		icmsAmount,
		icmsStAmount,
		ipiAmount,
		cbsAmount,
		ibsAmount,
		totalTaxesAmount,
		addedToTotalAmount,
		grandTotalAmount,
	};
}

export function emptyFiscalSummaryTotals(): IFiscalSummaryTotals {
	return {
		icmsAmount: 0,
		icmsStAmount: 0,
		ipiAmount: 0,
		cbsAmount: 0,
		ibsAmount: 0,
		totalTaxesAmount: 0,
		addedToTotalAmount: 0,
		grandTotalAmount: null,
	};
}

/** Display helper: backend may send 18 or 0.18. */
export function formatTaxRate(rate?: number | null): string {
	if (rate === null || rate === undefined || Number.isNaN(Number(rate))) {
		return '—';
	}
	const value = Number(rate);
	const percent = value <= 1 ? value * 100 : value;
	return `${percent.toLocaleString('pt-BR', {
		maximumFractionDigits: 4,
	})}%`;
}

export function hasAnyTaxAmount(taxes?: IFiscalTaxAmounts | null): boolean {
	if (!taxes) return false;
	return (
		Number(taxes.icmsAmount) > 0 ||
		Number(taxes.icmsStAmount) > 0 ||
		Number(taxes.ipiAmount) > 0 ||
		Number(taxes.cbsAmount) > 0 ||
		Number(taxes.ibsAmount) > 0
	);
}

export function hasFiscalBreakdown(totals: IFiscalSummaryTotals): boolean {
	return (
		totals.icmsAmount > 0 ||
		totals.icmsStAmount > 0 ||
		totals.ipiAmount > 0 ||
		totals.cbsAmount > 0 ||
		totals.ibsAmount > 0
	);
}
